// src/pages/ListingGeneratorPage.js
import React, { useState, useRef, useEffect } from 'react';
import { Upload, Copy, Check, Loader2, ShoppingBag, Store, Package, Image as ImageIcon, X, Wand2, Video, History, Trash2, BookMarked, Search, Save, Tag as TagIcon, ArrowLeft, Sparkles } from 'lucide-react';
import GeminiApiKeyInput from '../components/GeminiApiKeyInput';

const ListingGeneratorPage = () => {
    const [image, setImage] = useState(null);
    const [imageBase64, setImageBase64] = useState(null);
    const [context, setContext] = useState('');
    const [tone, setTone] = useState('Professional');
    const [brandName, setBrandName] = useState('');
    const [model, setModel] = useState('gemini-2.5-flash');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [activeTab, setActiveTab] = useState('amazon');
    const [selectedPlatforms, setSelectedPlatforms] = useState({
        amazon: true,
        etsy: true,
        ebay: true,
        tiktok: false
    });
    const [error, setError] = useState('');
    const [geminiApiKey, setGeminiApiKey] = useState('');
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [generationSuccess, setGenerationSuccess] = useState(false);

    const [library, setLibrary] = useState([]);
    const [showLibrary, setShowLibrary] = useState(false);
    const [librarySearch, setLibrarySearch] = useState('');
    const [libraryTagFilter, setLibraryTagFilter] = useState([]);
    const [libraryDetailId, setLibraryDetailId] = useState(null);
    const [detailDraft, setDetailDraft] = useState(null);

    const [isDragging, setIsDragging] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);

    const [saveFormOpen, setSaveFormOpen] = useState(false);
    const [saveFormSku, setSaveFormSku] = useState('');
    const [saveFormNotes, setSaveFormNotes] = useState('');
    const [saveFormTags, setSaveFormTags] = useState('');
    const [savedFlash, setSavedFlash] = useState(false);

    useEffect(() => {
        const saved = localStorage.getItem('listingHistory');
        if (saved) {
            try {
                setHistory(JSON.parse(saved));
            } catch (e) {
                console.error("Failed to parse history", e);
            }
        }
        const savedLib = localStorage.getItem('listingKeywordLibrary');
        if (savedLib) {
            try {
                setLibrary(JSON.parse(savedLib));
            } catch (e) {
                console.error("Failed to parse library", e);
            }
        }
    }, []);

    const persistLibrary = (updated) => {
        localStorage.setItem('listingKeywordLibrary', JSON.stringify(updated));
        return updated;
    };

    const saveToLibrary = () => {
        if (!result?.amazon?.title) return;
        const tagsArr = saveFormTags.split(',').map(t => t.trim()).filter(Boolean);
        const newEntry = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            thumbnail: image || null,
            productTitle: result.amazon.title,
            amazonKeywords: result.amazon.keywords || '',
            etsyTags: result.etsy?.tags || '',
            sku: saveFormSku.trim(),
            notes: saveFormNotes.trim(),
            tags: tagsArr
        };
        setLibrary(prev => persistLibrary([newEntry, ...prev].slice(0, 100)));
        setSaveFormOpen(false);
        setSaveFormSku('');
        setSaveFormNotes('');
        setSaveFormTags('');
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2000);
    };

    const updateLibraryItem = (id, patch) => {
        setLibrary(prev => persistLibrary(prev.map(it => it.id === id ? { ...it, ...patch } : it)));
    };

    const deleteLibraryItem = (id) => {
        setLibrary(prev => persistLibrary(prev.filter(it => it.id !== id)));
        if (libraryDetailId === id) {
            setLibraryDetailId(null);
            setDetailDraft(null);
        }
    };

    const clearLibrary = () => {
        if (window.confirm('Clear the entire keyword library? This cannot be undone.')) {
            setLibrary([]);
            localStorage.removeItem('listingKeywordLibrary');
            setLibraryDetailId(null);
            setDetailDraft(null);
        }
    };

    const openLibraryDetail = (item) => {
        setLibraryDetailId(item.id);
        setDetailDraft({
            sku: item.sku || '',
            notes: item.notes || '',
            tags: (item.tags || []).join(', ')
        });
    };

    const saveLibraryDetail = () => {
        if (!libraryDetailId || !detailDraft) return;
        const tagsArr = detailDraft.tags.split(',').map(t => t.trim()).filter(Boolean);
        updateLibraryItem(libraryDetailId, {
            sku: detailDraft.sku.trim(),
            notes: detailDraft.notes.trim(),
            tags: tagsArr
        });
        setLibraryDetailId(null);
        setDetailDraft(null);
    };

    const toggleTagFilter = (tag) => {
        setLibraryTagFilter(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    };

    const addToHistory = (newResult, currentContext, currentTone, currentBrand, currentPlatforms, currentModel) => {
        const newHistoryItem = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            context: currentContext,
            tone: currentTone,
            brandName: currentBrand,
            selectedPlatforms: currentPlatforms,
            result: newResult,
            model: currentModel
        };
        setHistory(prev => {
            const updatedHistory = [newHistoryItem, ...prev].slice(0, 50);
            localStorage.setItem('listingHistory', JSON.stringify(updatedHistory));
            return updatedHistory;
        });
    };

    const loadHistoryItem = (item) => {
        setContext(item.context);
        setTone(item.tone);
        setBrandName(item.brandName || '');
        setSelectedPlatforms(item.selectedPlatforms);
        setResult(item.result);
        setModel(item.model || 'gemini-2.5-flash');
        setShowHistory(false);
    };

    const deleteHistoryItem = (id) => {
        setHistory(prev => {
            const updatedHistory = prev.filter(item => item.id !== id);
            localStorage.setItem('listingHistory', JSON.stringify(updatedHistory));
            return updatedHistory;
        });
    };

    const clearHistory = () => {
        if (window.confirm('Are you sure you want to clear all history?')) {
            setHistory([]);
            localStorage.removeItem('listingHistory');
        }
    };

    const fileInputRef = useRef(null);

    const processFile = (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (PNG, JPG, or WEBP).');
            return;
        }
        setError('');
        const reader = new FileReader();
        reader.onloadend = () => {
            setImage(reader.result);
            const base64String = reader.result.split(',')[1];
            const mimeType = reader.result.split(';')[0].split(':')[1];
            setImageBase64({ data: base64String, mimeType });
        };
        reader.readAsDataURL(file);
    };

    const handleImageUpload = (e) => {
        processFile(e.target.files[0]);
    };

    const analyzeImage = async () => {
        if (!geminiApiKey) {
            setError("Please enter your Gemini API key first.");
            return;
        }
        if (!imageBase64) {
            setError("Please upload an image first.");
            return;
        }
        if (context.trim() && !window.confirm("Replace the existing product context with AI-generated text from the image?")) {
            return;
        }

        setAnalyzing(true);
        setError('');

        try {
            const prompt = `Analyze this product image and write a concise 2-4 sentence product description covering: what the product is, key materials visible, colors, notable features, and overall style/aesthetic. Write it as a single paragraph suitable for feeding to a listing generator. Do not include marketing language, prices, or made-up facts. Return only the description text, nothing else.`;

            const modelEndpoints = {
                'gemini-2.5-flash': 'gemini-2.5-flash',
                'gemini-flash-lite': 'gemini-3.1-flash-lite-preview',
                'gemini-flash': 'gemini-3-flash-preview',
                'gemini-pro': 'gemini-3.1-pro-preview'
            };
            const modelEndpoint = modelEndpoints[model] || 'gemini-2.5-flash';

            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/${modelEndpoint}:generateContent?key=${geminiApiKey}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        contents: [{
                            parts: [
                                { text: prompt },
                                { inline_data: { mime_type: imageBase64.mimeType, data: imageBase64.data } }
                            ]
                        }]
                    })
                }
            );

            if (!response.ok) throw new Error(`API Error: ${response.statusText}`);

            const data = await response.json();
            const text = data.candidates[0].content.parts[0].text.trim();
            setContext(text);
        } catch (err) {
            console.error(err);
            setError("Failed to analyze image. Please check your API key and try again.");
        } finally {
            setAnalyzing(false);
        }
    };

    const removeImage = () => {
        setImage(null);
        setImageBase64(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const generateListings = async () => {
        if (!geminiApiKey) {
            setError("Please enter your Gemini API key first.");
            return;
        }

        if (!imageBase64 && !context) {
            setError("Please upload an image or provide a description.");
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const promptText = `
        You are an expert E-commerce Copywriter specializing in Amazon, Etsy, and eBay SEO for 2024-2025.
        
        Analyze the provided product image (if available) and the following product context:
        "${context}"

        Adopt a "${tone}" brand voice for all written content.
        ${brandName ? `Brand Name: "${brandName}"` : ''}

        Generate distinct product listings for the following platforms based on the user's selection.
        Only generate content for the platforms requested below.

        STRICT REQUIREMENTS:
        ${brandName ? `
        - ALL Titles MUST start with the Brand Name: "${brandName}".
        - The character counts specified below include the Brand Name.
        ` : ''}

        ${selectedPlatforms.amazon ? `
        1. AMAZON LISTING:
           - Title: SEO optimized, STRICTLY under 125 characters. Includes key features/benefits.
           - Bullet Points: Exactly 5 bullet points highlighting features/benefits. MUST be plain text only — NO emojis, NO decorative unicode symbols, NO leading icons or pictographs of any kind. Each bullet should start with a capitalized keyword phrase followed by a colon, then the benefit. Example format: "DURABLE CONSTRUCTION: Made from solid oak for long-lasting use."
           - Description: Professional, detailed, persuasive, HTML formatted with simple line breaks.
           - Search Terms (Keywords): Backend keywords, max 500 bytes (approx 500 chars), separated by semicolons followed by a space (; ). High search volume terms.
        ` : ''}

        ${selectedPlatforms.etsy ? `
        2. ETSY LISTING:
           - Title: SEO optimized, uses long-tail keywords.
           - Description: Personal, engaging tone. MUST explicitly state: "This item strictly follows Etsy's handmade policy and guidelines." somewhere naturally in the text.
           - Tags: Exactly 13 tags, separated by commas (,).
        ` : ''}

        ${selectedPlatforms.ebay ? `
        3. EBAY LISTING:
           - Title: SEO optimized, max 80 characters. Include brand, key features, condition.
           - Subtitle: Optional 55 character subtitle for additional keywords.
           - Description: Professional HTML formatted description with product details, features, and benefits.
           - Item Specifics: Key-value pairs like "Brand: ...", "Condition: New", "Color: ...", etc. (5-10 specifics).
           - Shipping: Brief shipping policy statement.
        ` : ''}

        ${selectedPlatforms.tiktok ? `
        4. TIKTOK SHOP LISTING:
           - Title: SEO optimized, max 255 characters. Engaging and descriptive.
           - Description: Conversational, energetic, and persuasive. Use emojis where appropriate. Focus on benefits and "viral" appeal.
           - Attributes: Key product attributes (e.g., Material, Pattern, Season).
        ` : ''}

        Return the output as a valid JSON object with this structure (omit keys for unselected platforms):
        {
          ${selectedPlatforms.amazon ? `"amazon": {
            "title": "...",
            "bullets": ["...", ...],
            "description": "...",
            "keywords": "..."
          },` : ''}
          ${selectedPlatforms.etsy ? `"etsy": {
            "title": "...",
            "description": "...",
            "tags": "..."
          },` : ''}
          ${selectedPlatforms.ebay ? `"ebay": {
            "title": "...",
            "subtitle": "...",
            "description": "...",
            "itemSpecifics": {
              "Brand": "...",
              "Condition": "...",
              "Color": "...",
              "Material": "...",
              "Size": "..."
            },
            "shipping": "..."
          },` : ''}
          ${selectedPlatforms.tiktok ? `"tiktok": {
            "title": "...",
            "description": "...",
            "attributes": "..."
          }` : ''}
        }
        Do not include markdown formatting (like \`\`\`json) in the response, just the raw JSON string.
      `;

            const requestBody = {
                contents: [
                    {
                        parts: [
                            { text: promptText },
                            ...(imageBase64 ? [{
                                inline_data: {
                                    mime_type: imageBase64.mimeType,
                                    data: imageBase64.data
                                }
                            }] : [])
                        ]
                    }
                ],
                generationConfig: {
                    response_mime_type: "application/json"
                }
            };

            const modelEndpoints = {
                'gemini-2.5-flash': 'gemini-2.5-flash',
                'gemini-flash-lite': 'gemini-3.1-flash-lite-preview',
                'gemini-flash': 'gemini-3-flash-preview',
                'gemini-pro': 'gemini-3.1-pro-preview'
            };
            const modelEndpoint = modelEndpoints[model] || 'gemini-2.5-flash';

            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/${modelEndpoint}:generateContent?key=${geminiApiKey}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody)
                }
            );

            if (!response.ok) throw new Error(`API Error: ${response.statusText}`);

            const data = await response.json();
            const generatedText = data.candidates[0].content.parts[0].text;

            const cleanJson = generatedText.replace(/```json/g, '').replace(/```/g, '').trim();
            const parsedResult = JSON.parse(cleanJson);

            setResult(parsedResult);
            addToHistory(parsedResult, context, tone, brandName, selectedPlatforms, model);
            setGenerationSuccess(true);
            setTimeout(() => setGenerationSuccess(false), 3000);
        } catch (err) {
            console.error(err);
            setError("Failed to generate listings. Please check your API key and try again.");
        } finally {
            setLoading(false);
        }
    };

    const CopyButton = ({ text, label }) => {
        const [copied, setCopied] = useState(false);

        const handleCopy = async () => {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    // Fallback for older browsers
                    const textarea = document.createElement('textarea');
                    textarea.value = text;
                    textarea.style.position = 'fixed';
                    textarea.style.opacity = '0';
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textarea);
                }
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                console.error('Failed to copy', err);
            }
        };

        return (
            <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors px-2 py-1 rounded bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/20"
                title="Copy to clipboard"
                aria-label={label ? `Copy ${label}` : 'Copy to clipboard'}
            >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied!' : label || 'Copy'}
            </button>
        );
    };

    return (
        <div className="flex-grow bg-gradient-to-br from-gray-900 via-slate-900 to-black min-h-screen text-slate-200 p-4 md:p-8 font-sans selection:bg-indigo-500/30 animate-fade-in">
            <div className="max-w-6xl mx-auto space-y-8 relative">
                <div className="absolute top-0 right-0 flex gap-2">
                    <button
                        onClick={() => setShowLibrary(true)}
                        className="p-2 text-slate-400 hover:text-white bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700 transition-all flex items-center gap-2 text-sm font-medium"
                    >
                        <BookMarked size={18} />
                        <span className="hidden sm:inline">Library</span>
                        {library.length > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                                {library.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setShowHistory(true)}
                        className="p-2 text-slate-400 hover:text-white bg-slate-800/50 hover:bg-slate-700/50 rounded-lg border border-slate-700 transition-all flex items-center gap-2 text-sm font-medium"
                    >
                        <History size={18} />
                        <span className="hidden sm:inline">History</span>
                    </button>
                </div>

                {/* Header */}
                <div className="text-center space-y-2">
                    <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-400 to-cyan-400 bg-clip-text text-transparent">
                        Listing Generator AI
                    </h1>
                    <p className="text-slate-400">Create SEO-optimized Amazon, Etsy & eBay listings from a single image.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                    {/* Left Column: Inputs */}
                    <div className="lg:col-span-4 space-y-6">

                        {/* API Key Input */}
                        <GeminiApiKeyInput onApiKeyChange={setGeminiApiKey} />

                        {/* Image Upload Card */}
                        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 shadow-xl backdrop-blur-md animate-slide-up">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-white">
                                <ImageIcon size={20} className="text-indigo-400" />
                                1. Product Image
                            </h2>

                            <div
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={(e) => {
                                    if (e.currentTarget.contains(e.relatedTarget)) return;
                                    setIsDragging(false);
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    setIsDragging(false);
                                    processFile(e.dataTransfer.files[0]);
                                }}
                                className={`relative border-2 border-dashed rounded-lg transition-all duration-200 ${isDragging
                                    ? 'border-indigo-400 bg-indigo-500/10 scale-[1.01]'
                                    : image ? 'border-indigo-500/50 bg-indigo-500/5' : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/30'
                                    }`}
                            >
                                {image ? (
                                    <div className="relative group">
                                        <img
                                            src={image}
                                            alt="Preview"
                                            className="w-full h-64 object-contain rounded-lg p-2"
                                        />
                                        <button
                                            onClick={removeImage}
                                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                                            aria-label="Remove image"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center h-64 cursor-pointer p-4">
                                        <Upload className="w-10 h-10 text-slate-500 mb-3" />
                                        <span className="text-sm font-medium text-slate-300 text-center">Click or drag image here</span>
                                        <span className="text-xs text-slate-500 mt-1">Supports PNG, JPG, WEBP</span>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                        />
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Context Input Card */}
                        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 shadow-xl backdrop-blur-md animate-slide-up">
                            <div className="flex items-center justify-between mb-4 gap-2">
                                <h2 className="text-lg font-semibold flex items-center gap-2 text-white">
                                    <Wand2 size={20} className="text-indigo-400" />
                                    2. Product Context
                                </h2>
                                <button
                                    onClick={analyzeImage}
                                    disabled={analyzing || !imageBase64 || !geminiApiKey}
                                    title={!imageBase64 ? 'Upload an image first' : !geminiApiKey ? 'Enter your Gemini API key first' : 'Generate context from the uploaded image'}
                                    className="flex items-center gap-1.5 text-xs font-medium text-indigo-400 hover:text-indigo-300 disabled:text-slate-500 disabled:hover:text-slate-500 transition-colors px-2.5 py-1.5 rounded bg-indigo-500/10 hover:bg-indigo-500/20 disabled:bg-slate-800/50 disabled:cursor-not-allowed border border-indigo-500/20 disabled:border-slate-700 whitespace-nowrap"
                                >
                                    {analyzing ? (
                                        <><Loader2 size={14} className="animate-spin" /> Analyzing...</>
                                    ) : (
                                        <><Sparkles size={14} /> Analyze Image</>
                                    )}
                                </button>
                            </div>
                            <textarea
                                value={context}
                                onChange={(e) => setContext(e.target.value)}
                                placeholder="e.g. Hand-carved wooden bear statue, rustic decor, made of oak, finished with beeswax. Good for cabin aesthetic..."
                                className="w-full h-40 bg-slate-900/50 border border-slate-600 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none placeholder-slate-500 text-slate-200"
                            />
                        </div>

                        {/* Configuration Card */}
                        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-5 shadow-xl backdrop-blur-md space-y-4 animate-slide-up">
                            <div>
                                <label className="block text-sm font-semibold mb-2 text-white">Brand Name (Optional)</label>
                                <input
                                    type="text"
                                    value={brandName}
                                    onChange={(e) => setBrandName(e.target.value)}
                                    placeholder="e.g. Acme Corp"
                                    className="w-full p-3 bg-slate-900/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold mb-2 text-white">Brand Voice</label>
                                <select
                                    value={tone}
                                    onChange={(e) => setTone(e.target.value)}
                                    className="w-full p-3 bg-slate-900/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200"
                                >
                                    <option value="Professional">Professional (Default)</option>
                                    <option value="Luxury">Luxury & Premium</option>
                                    <option value="Friendly">Friendly & Playful</option>
                                    <option value="Minimalist">Minimalist & Clean</option>
                                    <option value="Persuasive">Persuasive & Salesy</option>
                                    <option value="Eco-Friendly">Eco-Friendly & Natural</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold mb-2 text-white">Target Platforms</label>
                                <div className="grid grid-cols-2 gap-2">
                                    <label className="flex items-center space-x-2 bg-slate-900/50 p-2 rounded-lg border border-slate-600 cursor-pointer hover:bg-slate-800 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={selectedPlatforms.amazon}
                                            onChange={(e) => setSelectedPlatforms({ ...selectedPlatforms, amazon: e.target.checked })}
                                            className="rounded border-slate-500 text-indigo-500 focus:ring-indigo-500 bg-slate-800"
                                        />
                                        <span className="text-sm text-slate-200">Amazon</span>
                                    </label>
                                    <label className="flex items-center space-x-2 bg-slate-900/50 p-2 rounded-lg border border-slate-600 cursor-pointer hover:bg-slate-800 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={selectedPlatforms.etsy}
                                            onChange={(e) => setSelectedPlatforms({ ...selectedPlatforms, etsy: e.target.checked })}
                                            className="rounded border-slate-500 text-orange-500 focus:ring-orange-500 bg-slate-800"
                                        />
                                        <span className="text-sm text-slate-200">Etsy</span>
                                    </label>
                                    <label className="flex items-center space-x-2 bg-slate-900/50 p-2 rounded-lg border border-slate-600 cursor-pointer hover:bg-slate-800 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={selectedPlatforms.ebay}
                                            onChange={(e) => setSelectedPlatforms({ ...selectedPlatforms, ebay: e.target.checked })}
                                            className="rounded border-slate-500 text-blue-500 focus:ring-blue-500 bg-slate-800"
                                        />
                                        <span className="text-sm text-slate-200">eBay</span>
                                    </label>
                                    <label className="flex items-center space-x-2 bg-slate-900/50 p-2 rounded-lg border border-slate-600 cursor-pointer hover:bg-slate-800 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={selectedPlatforms.tiktok}
                                            onChange={(e) => setSelectedPlatforms({ ...selectedPlatforms, tiktok: e.target.checked })}
                                            className="rounded border-slate-500 text-pink-500 focus:ring-pink-500 bg-slate-800"
                                        />
                                        <span className="text-sm text-slate-200">TikTok Shop</span>
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold mb-2 text-white">Model</label>
                                <select
                                    value={model}
                                    onChange={(e) => setModel(e.target.value)}
                                    className="w-full p-3 bg-slate-900/50 border border-slate-600 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200"
                                >
                                    <option value="gemini-2.5-flash">Gemini 2.5 Flash — $0.15/$0.60 per MTok</option>
                                    <option value="gemini-flash-lite">Gemini 3.1 Flash Lite — $0.25/$1.50 per MTok</option>
                                    <option value="gemini-flash">Gemini 3 Flash — $0.50/$3.00 per MTok</option>
                                    <option value="gemini-pro">Gemini 3.1 Pro — $2.00/$12.00 per MTok</option>
                                </select>
                            </div>
                        </div>

                        <button
                            onClick={generateListings}
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-indigo-900/20 transition-all transform active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin" /> Generating...
                                </>
                            ) : (
                                <>
                                    Generate Listings
                                </>
                            )}
                        </button>

                        {generationSuccess && (
                            <div className="p-3 bg-green-500/10 border border-green-500/20 text-green-400 text-sm rounded-lg text-center flex items-center justify-center gap-2 animate-in fade-in duration-300">
                                <Check size={16} /> Listings generated successfully!
                            </div>
                        )}

                        {error && (
                            <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg text-center">
                                {error}
                            </div>
                        )}
                    </div>

                    {/* Right Column: Output */}
                    <div className="lg:col-span-8">
                        {result ? (
                            <div className="bg-slate-800/50 rounded-xl border border-slate-700 shadow-xl backdrop-blur-md overflow-hidden flex flex-col h-full min-h-[600px] animate-slide-up">
                                {/* Tabs */}
                                <div className="flex border-b border-slate-700" role="tablist">
                                    <button
                                        onClick={() => setActiveTab('amazon')}
                                        role="tab"
                                        aria-selected={activeTab === 'amazon'}
                                        className={`flex-1 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2 text-xs sm:text-sm ${activeTab === 'amazon'
                                            ? 'border-indigo-500 text-indigo-400 bg-slate-800'
                                            : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                                            }`}
                                    >
                                        <ShoppingBag size={18} /> Amazon
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('etsy')}
                                        role="tab"
                                        aria-selected={activeTab === 'etsy'}
                                        className={`flex-1 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2 text-xs sm:text-sm ${activeTab === 'etsy'
                                            ? 'border-orange-500 text-orange-400 bg-slate-800'
                                            : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                                            }`}
                                    >
                                        <Store size={18} /> Etsy
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('ebay')}
                                        role="tab"
                                        aria-selected={activeTab === 'ebay'}
                                        className={`flex-1 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2 text-xs sm:text-sm ${activeTab === 'ebay'
                                            ? 'border-blue-500 text-blue-400 bg-slate-800'
                                            : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                                            }`}
                                    >
                                        <Package size={18} /> eBay
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('tiktok')}
                                        role="tab"
                                        aria-selected={activeTab === 'tiktok'}
                                        className={`flex-1 py-4 flex items-center justify-center gap-2 font-medium transition-colors border-b-2 text-xs sm:text-sm ${activeTab === 'tiktok'
                                            ? 'border-pink-500 text-pink-400 bg-slate-800'
                                            : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                                            }`}
                                    >
                                        <Video size={18} /> TikTok
                                    </button>
                                </div>

                                {/* Content Area */}
                                <div className="p-6 overflow-y-auto flex-grow" role="tabpanel">
                                    {activeTab === 'amazon' && result.amazon && (
                                        <div className="space-y-6 animate-in fade-in duration-300">
                                            {/* Save to Library */}
                                            <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-lg p-4">
                                                {!saveFormOpen ? (
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-2 text-sm text-slate-300">
                                                            <BookMarked size={16} className="text-indigo-400" />
                                                            <span>Save these keywords for later upload to Amazon.</span>
                                                        </div>
                                                        <button
                                                            onClick={() => setSaveFormOpen(true)}
                                                            className="flex items-center gap-1.5 text-xs font-medium text-white px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 transition-colors whitespace-nowrap"
                                                        >
                                                            {savedFlash ? <><Check size={14} /> Saved</> : <><Save size={14} /> Save to Library</>}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-3">
                                                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                                                            <Save size={16} className="text-indigo-400" /> Save to Library
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            <input
                                                                type="text"
                                                                value={saveFormSku}
                                                                onChange={(e) => setSaveFormSku(e.target.value)}
                                                                placeholder="SKU (optional, e.g. WNK310802)"
                                                                className="w-full p-2 text-sm bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={saveFormTags}
                                                                onChange={(e) => setSaveFormTags(e.target.value)}
                                                                placeholder="Tags (comma-separated, e.g. necklaces, rodeo)"
                                                                className="w-full p-2 text-sm bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500"
                                                            />
                                                        </div>
                                                        <textarea
                                                            value={saveFormNotes}
                                                            onChange={(e) => setSaveFormNotes(e.target.value)}
                                                            placeholder="Notes (optional)"
                                                            rows={2}
                                                            className="w-full p-2 text-sm bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500 resize-none"
                                                        />
                                                        <div className="flex gap-2 justify-end">
                                                            <button
                                                                onClick={() => { setSaveFormOpen(false); setSaveFormSku(''); setSaveFormNotes(''); setSaveFormTags(''); }}
                                                                className="text-xs font-medium text-slate-300 px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                onClick={saveToLibrary}
                                                                className="flex items-center gap-1.5 text-xs font-medium text-white px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 transition-colors"
                                                            >
                                                                <Save size={14} /> Save
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Amazon Title */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Product Title ({result.amazon.title.length} chars)</label>
                                                    <CopyButton text={result.amazon.title} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed">
                                                    {result.amazon.title}
                                                </div>
                                            </div>

                                            {/* Amazon Bullets */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Bullet Points</label>
                                                    <CopyButton text={result.amazon.bullets.join('\n')} label="Copy All Bullets" />
                                                </div>
                                                <div className="space-y-2">
                                                    {result.amazon.bullets.map((bullet, idx) => (
                                                        <div key={idx} className="flex gap-3 p-3 bg-slate-900 rounded-lg border border-slate-700 items-start group">
                                                            <span className="text-indigo-500 font-bold select-none mt-1">{idx + 1}.</span>
                                                            <span className="text-sm flex-grow pt-0.5">{bullet}</span>
                                                            <div className="opacity-70 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                                                <CopyButton text={bullet} label="Copy" />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Amazon Description */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Product Description</label>
                                                    <CopyButton text={result.amazon.description} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                                                    {result.amazon.description}
                                                </div>
                                            </div>

                                            {/* Amazon Keywords */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Backend Search Terms</label>
                                                    <CopyButton text={result.amazon.keywords} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm font-mono text-indigo-300 break-words">
                                                    {result.amazon.keywords}
                                                </div>
                                                <p className="text-xs text-slate-500">Separated by semicolons (;), max 500 bytes.</p>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'etsy' && result.etsy && (
                                        <div className="space-y-6 animate-in fade-in duration-300">
                                            {/* Etsy Title */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Listing Title</label>
                                                    <CopyButton text={result.etsy.title} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed">
                                                    {result.etsy.title}
                                                </div>
                                            </div>

                                            {/* Etsy Tags */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">13 Listing Tags</label>
                                                    <CopyButton text={result.etsy.tags} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                                                    <div className="flex flex-wrap gap-2">
                                                        {result.etsy.tags.split(',').map((tag, i) => (
                                                            <span key={i} className="px-2 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs rounded-md">
                                                                {tag.trim()}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Etsy Description */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Description</label>
                                                    <CopyButton text={result.etsy.description} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                                                    {result.etsy.description}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'ebay' && result.ebay && (
                                        <div className="space-y-6 animate-in fade-in duration-300">
                                            {/* eBay Title */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Title ({result.ebay.title.length}/80 chars)</label>
                                                    <CopyButton text={result.ebay.title} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed">
                                                    {result.ebay.title}
                                                </div>
                                            </div>

                                            {/* eBay Subtitle */}
                                            {result.ebay.subtitle && (
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Subtitle ({result.ebay.subtitle.length}/55 chars)</label>
                                                        <CopyButton text={result.ebay.subtitle} />
                                                    </div>
                                                    <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed text-slate-300">
                                                        {result.ebay.subtitle}
                                                    </div>
                                                </div>
                                            )}

                                            {/* eBay Item Specifics */}
                                            {result.ebay.itemSpecifics && (
                                                <div className="space-y-2">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Item Specifics</label>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {Object.entries(result.ebay.itemSpecifics).map(([key, value]) => (
                                                            <div key={key} className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                                                                <div className="text-xs text-slate-500 mb-1">{key}</div>
                                                                <div className="text-sm text-blue-300 font-medium">{value}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* eBay Description */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Description</label>
                                                    <CopyButton text={result.ebay.description} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                                                    {result.ebay.description}
                                                </div>
                                            </div>

                                            {/* eBay Shipping */}
                                            {result.ebay.shipping && (
                                                <div className="space-y-2">
                                                    <div className="flex justify-between items-center">
                                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Shipping Policy</label>
                                                        <CopyButton text={result.ebay.shipping} />
                                                    </div>
                                                    <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed">
                                                        {result.ebay.shipping}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'tiktok' && result.tiktok && (
                                        <div className="space-y-6 animate-in fade-in duration-300">
                                            {/* TikTok Title */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Title ({result.tiktok.title.length}/255 chars)</label>
                                                    <CopyButton text={result.tiktok.title} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed">
                                                    {result.tiktok.title}
                                                </div>
                                            </div>

                                            {/* TikTok Attributes */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Attributes</label>
                                                    <CopyButton text={result.tiktok.attributes} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed text-pink-300">
                                                    {result.tiktok.attributes}
                                                </div>
                                            </div>

                                            {/* TikTok Description */}
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Description</label>
                                                    <CopyButton text={result.tiktok.description} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm leading-relaxed whitespace-pre-wrap">
                                                    {result.tiktok.description}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="h-full min-h-[400px] border-2 border-dashed border-slate-700 rounded-xl flex flex-col items-center justify-center text-slate-500 p-8 bg-slate-800/30">
                                <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                                    <Wand2 className="text-indigo-500 opacity-50" size={32} />
                                </div>
                                <h3 className="text-lg font-medium text-slate-400 mb-2">Ready to Generate</h3>
                                <p className="text-center max-w-md">Upload an image and provide a short description to generate professional listings tailored for Amazon, Etsy, and eBay.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Keyword Library Modal */}
            {showLibrary && (() => {
                const allTags = Array.from(new Set(library.flatMap(it => it.tags || []))).sort();
                const q = librarySearch.trim().toLowerCase();
                const filtered = library.filter(it => {
                    if (libraryTagFilter.length > 0) {
                        const itTags = it.tags || [];
                        if (!libraryTagFilter.every(t => itTags.includes(t))) return false;
                    }
                    if (q) {
                        const hay = [it.productTitle, it.sku, it.notes, ...(it.tags || [])].join(' ').toLowerCase();
                        if (!hay.includes(q)) return false;
                    }
                    return true;
                });
                const detailEntry = libraryDetailId ? library.find(it => it.id === libraryDetailId) : null;

                const closeModal = () => {
                    setShowLibrary(false);
                    setLibraryDetailId(null);
                    setDetailDraft(null);
                };

                return (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-start justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
                        <div className="w-full max-w-6xl bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 my-8 animate-in zoom-in-95 duration-200">
                            {!detailEntry ? (
                                <>
                                    {/* Header */}
                                    <div className="flex justify-between items-center p-6 border-b border-slate-700">
                                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                            <BookMarked className="text-indigo-400" /> Keyword Library
                                            <span className="text-sm font-normal text-slate-400">{library.length} saved</span>
                                        </h2>
                                        <button
                                            onClick={closeModal}
                                            className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
                                            aria-label="Close library"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>

                                    {/* Toolbar */}
                                    {library.length > 0 && (
                                        <div className="p-6 border-b border-slate-700 space-y-4">
                                            <div className="flex gap-3 items-center">
                                                <div className="relative flex-grow">
                                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                                                    <input
                                                        type="text"
                                                        value={librarySearch}
                                                        onChange={(e) => setLibrarySearch(e.target.value)}
                                                        placeholder="Search by title, SKU, notes, or tags..."
                                                        className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 outline-none"
                                                    />
                                                </div>
                                                <button
                                                    onClick={clearLibrary}
                                                    className="text-xs font-medium text-red-400 hover:bg-red-500/10 border border-red-500/20 px-3 py-2 rounded-lg transition-colors flex items-center gap-1.5 whitespace-nowrap"
                                                >
                                                    <Trash2 size={14} /> Clear All
                                                </button>
                                            </div>

                                            {allTags.length > 0 && (
                                                <div className="flex flex-wrap gap-2 items-center">
                                                    <TagIcon size={14} className="text-slate-500" />
                                                    <button
                                                        onClick={() => setLibraryTagFilter([])}
                                                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${libraryTagFilter.length === 0
                                                            ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                                                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
                                                            }`}
                                                    >
                                                        All
                                                    </button>
                                                    {allTags.map(tag => (
                                                        <button
                                                            key={tag}
                                                            onClick={() => toggleTagFilter(tag)}
                                                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${libraryTagFilter.includes(tag)
                                                                ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                                                                : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-slate-200'
                                                                }`}
                                                        >
                                                            {tag}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Grid */}
                                    <div className="p-6">
                                        {library.length === 0 ? (
                                            <div className="text-center py-16 text-slate-500">
                                                <BookMarked className="w-16 h-16 mx-auto mb-4 opacity-20" />
                                                <h3 className="text-lg font-medium text-slate-400 mb-2">Your library is empty</h3>
                                                <p className="text-sm max-w-md mx-auto">Generate a listing and click "Save to Library" on the Amazon result to add keywords here.</p>
                                            </div>
                                        ) : filtered.length === 0 ? (
                                            <div className="text-center py-16 text-slate-500">
                                                <Search className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                                <p className="text-sm">No entries match your search or filters.</p>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {filtered.map(item => (
                                                    <div
                                                        key={item.id}
                                                        onClick={() => openLibraryDetail(item)}
                                                        className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-indigo-500/50 transition-colors cursor-pointer group relative"
                                                    >
                                                        {item.thumbnail ? (
                                                            <img src={item.thumbnail} alt={item.productTitle} className="w-full h-40 object-cover bg-slate-900" />
                                                        ) : (
                                                            <div className="w-full h-40 bg-slate-900 flex items-center justify-center">
                                                                <ImageIcon className="text-slate-700" size={40} />
                                                            </div>
                                                        )}
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); deleteLibraryItem(item.id); }}
                                                            className="absolute top-2 right-2 p-1.5 bg-slate-900/80 text-slate-400 hover:text-red-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                            title="Delete"
                                                            aria-label="Delete entry"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                        <div className="p-4 space-y-2">
                                                            <h3 className="text-sm font-semibold text-white line-clamp-2 leading-snug">
                                                                {item.productTitle}
                                                            </h3>
                                                            {item.sku && (
                                                                <div className="text-xs font-mono text-slate-400">{item.sku}</div>
                                                            )}
                                                            {item.tags && item.tags.length > 0 && (
                                                                <div className="flex flex-wrap gap-1">
                                                                    {item.tags.map(tag => (
                                                                        <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            <div className="text-[10px] text-slate-500 font-mono pt-1">
                                                                {new Date(item.timestamp).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                /* Detail view */
                                <>
                                    <div className="flex justify-between items-center p-6 border-b border-slate-700">
                                        <button
                                            onClick={() => { setLibraryDetailId(null); setDetailDraft(null); }}
                                            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
                                        >
                                            <ArrowLeft size={18} /> Back to Library
                                        </button>
                                        <button
                                            onClick={closeModal}
                                            className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
                                            aria-label="Close library"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>

                                    <div className="p-6 space-y-6">
                                        {detailEntry.thumbnail && (
                                            <img
                                                src={detailEntry.thumbnail}
                                                alt={detailEntry.productTitle}
                                                className="w-full max-h-64 object-contain bg-slate-950 rounded-lg border border-slate-700"
                                            />
                                        )}

                                        <h2 className="text-lg font-bold text-white">{detailEntry.productTitle}</h2>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">SKU</label>
                                                <input
                                                    type="text"
                                                    value={detailDraft?.sku || ''}
                                                    onChange={(e) => setDetailDraft(d => ({ ...d, sku: e.target.value }))}
                                                    placeholder="e.g. WNK310802"
                                                    className="w-full p-2 text-sm bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 font-mono placeholder-slate-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Tags (comma-separated)</label>
                                                <input
                                                    type="text"
                                                    value={detailDraft?.tags || ''}
                                                    onChange={(e) => setDetailDraft(d => ({ ...d, tags: e.target.value }))}
                                                    placeholder="necklaces, rodeo"
                                                    className="w-full p-2 text-sm bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Notes</label>
                                            <textarea
                                                value={detailDraft?.notes || ''}
                                                onChange={(e) => setDetailDraft(d => ({ ...d, notes: e.target.value }))}
                                                rows={3}
                                                className="w-full p-2 text-sm bg-slate-900/50 border border-slate-600 rounded focus:ring-2 focus:ring-indigo-500 outline-none text-slate-200 placeholder-slate-500 resize-none"
                                            />
                                        </div>

                                        {detailEntry.amazonKeywords && (
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Amazon Backend Keywords</label>
                                                    <CopyButton text={detailEntry.amazonKeywords} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700 text-sm font-mono text-indigo-300 break-words">
                                                    {detailEntry.amazonKeywords}
                                                </div>
                                            </div>
                                        )}

                                        {detailEntry.etsyTags && (
                                            <div className="space-y-2">
                                                <div className="flex justify-between items-center">
                                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Etsy Tags</label>
                                                    <CopyButton text={detailEntry.etsyTags} />
                                                </div>
                                                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                                                    <div className="flex flex-wrap gap-2">
                                                        {detailEntry.etsyTags.split(',').map((tag, i) => (
                                                            <span key={i} className="px-2 py-1 bg-orange-500/10 border border-orange-500/30 text-orange-300 text-xs rounded-md">
                                                                {tag.trim()}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex flex-wrap gap-2 justify-between pt-4 border-t border-slate-700">
                                            <button
                                                onClick={() => deleteLibraryItem(detailEntry.id)}
                                                className="flex items-center gap-1.5 text-xs font-medium text-red-400 hover:bg-red-500/10 border border-red-500/20 px-4 py-2 rounded transition-colors"
                                            >
                                                <Trash2 size={14} /> Delete Entry
                                            </button>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => { setLibraryDetailId(null); setDetailDraft(null); }}
                                                    className="text-xs font-medium text-slate-300 px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={saveLibraryDetail}
                                                    className="flex items-center gap-1.5 text-xs font-medium text-white px-4 py-2 rounded bg-indigo-600 hover:bg-indigo-500 transition-colors"
                                                >
                                                    <Save size={14} /> Save Changes
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                );
            })()}

            {/* History Sidebar */}
            {showHistory && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 flex justify-end transition-opacity animate-in fade-in duration-200">
                    <div className="w-full max-w-md bg-slate-900 h-full shadow-2xl p-6 overflow-y-auto border-l border-slate-700 animate-in slide-in-from-right duration-300">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <History className="text-indigo-400" /> History
                            </h2>
                            <button
                                onClick={() => setShowHistory(false)}
                                className="p-2 hover:bg-slate-800 rounded-full text-slate-400 hover:text-white transition-colors"
                                aria-label="Close history"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {history.length === 0 ? (
                                <div className="text-center py-12 text-slate-500">
                                    <History className="w-12 h-12 mx-auto mb-3 opacity-20" />
                                    <p>No history yet.</p>
                                    <p className="text-xs mt-1">Generate some listings to see them here.</p>
                                </div>
                            ) : (
                                history.map(item => (
                                    <div key={item.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-indigo-500/50 transition-colors group relative">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-xs text-slate-500 font-mono">
                                                {new Date(item.timestamp).toLocaleDateString()} {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </span>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); deleteHistoryItem(item.id); }}
                                                className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1"
                                                title="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                        <p className="text-sm text-slate-300 line-clamp-2 mb-3 font-medium">
                                            {item.context || "No context provided"}
                                        </p>
                                        <div className="flex flex-wrap gap-1 mb-3">
                                            {Object.keys(item.selectedPlatforms).filter(k => item.selectedPlatforms[k]).map(p => (
                                                <span key={p} className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">
                                                    {p}
                                                </span>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => loadHistoryItem(item)}
                                            className="w-full bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 text-xs py-2 rounded border border-indigo-500/20 transition-colors font-medium"
                                        >
                                            Load Result
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>

                        {history.length > 0 && (
                            <button
                                onClick={clearHistory}
                                className="w-full mt-8 py-3 text-sm text-red-400 hover:bg-red-500/10 border border-red-500/20 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                <Trash2 size={16} /> Clear All History
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ListingGeneratorPage;
